/**
 * The register table's column choice, as React state.
 *
 * The rules live next door in `columnPrefs.ts` (pure, tested); this is only the
 * hook that holds them. Read once with the initialiser rather than in an effect:
 * reading in an effect paints the default table and then re-lays it out a frame
 * later, which on a ten-column grid is the whole page jumping.
 */
import { useCallback, useMemo, useState } from "react";
import {
  AR_COLUMNS, DEFAULT_COLUMN_KEYS, LOCKED_COLUMN_KEYS, type ArColumn,
} from "./columns";
import { normalizeColumns, readStoredColumns, writeStoredColumns } from "./columnPrefs";

const KNOWN = AR_COLUMNS.map((c) => c.key);

export interface ColumnPrefs {
  /** The visible columns, in table order. */
  shown: ArColumn[];
  visible: Set<string>;
  toggle: (key: string) => void;
  reset: () => void;
  /** True while the layout differs from the default one - the picker's trigger
   *  says so, or nobody can tell a tidied table from a broken one. */
  changed: boolean;
}

export function useColumnPrefs(): ColumnPrefs {
  const [keys, setKeys] = useState<string[]>(() =>
    normalizeColumns(readStoredColumns(), KNOWN, LOCKED_COLUMN_KEYS, DEFAULT_COLUMN_KEYS),
  );

  const apply = useCallback((next: string[]) => {
    const safe = normalizeColumns(next, KNOWN, LOCKED_COLUMN_KEYS, DEFAULT_COLUMN_KEYS);
    writeStoredColumns(safe, DEFAULT_COLUMN_KEYS);
    setKeys(safe);
  }, []);

  const toggle = useCallback(
    (key: string) => {
      if (LOCKED_COLUMN_KEYS.includes(key)) return;
      // Read off the current list rather than the state variable, so two clicks
      // in one tick cannot both start from the same layout.
      setKeys((cur) => {
        const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
        const safe = normalizeColumns(next, KNOWN, LOCKED_COLUMN_KEYS, DEFAULT_COLUMN_KEYS);
        writeStoredColumns(safe, DEFAULT_COLUMN_KEYS);
        return safe;
      });
    },
    [],
  );

  const reset = useCallback(() => apply(DEFAULT_COLUMN_KEYS), [apply]);

  const visible = useMemo(() => new Set(keys), [keys]);
  const shown = useMemo(() => AR_COLUMNS.filter((c) => visible.has(c.key)), [visible]);
  const changed =
    keys.length !== DEFAULT_COLUMN_KEYS.length || keys.some((k) => !DEFAULT_COLUMN_KEYS.includes(k));

  return { shown, visible, toggle, reset, changed };
}
